/**
 * Packaging helpers for the shared scroll-animation runtime.
 *
 * Detection is derived from the motion registry (M5): every marker of every
 * pattern with `support: 'runtime'`. Static and none patterns are parse-time
 * concerns — a collapsed carousel needs its marker CSS, not this runtime.
 * Before M5 this list was a hardcoded pair (`[data-aos]`, `.translate`), so a
 * convention the preview animated silently shipped nothing in exports.
 */

import { getScrollAnimationRuntimeSource } from '../shared/scroll-animation-runtime-source.js';
import { MOTION_PATTERNS } from '../shared/motion-patterns.js';
import {
  effectiveEntranceFor,
  stateUsesEntrance,
  sectionElementEntranceMap
} from '../shared/entrance-presets.js';

const RUNTIME_MARKER_SELECTORS = MOTION_PATTERNS.filter(
  (pattern) => pattern.support === 'runtime'
).flatMap((pattern) => pattern.markers || []);

/**
 * Bare identifiers for the DOMParser-less fallback: class/id/attribute names
 * extracted from the marker selectors, minus pseudo-class keywords. These are
 * our own registry constants, not CSS being parsed.
 */
const SELECTOR_PSEUDO_KEYWORDS = new Set(['has', 'not', 'is', 'where']);
const RUNTIME_MARKER_TOKENS = Array.from(
  new Set(
    RUNTIME_MARKER_SELECTORS.flatMap((selector) => selector.match(/[a-zA-Z][\w-]*/g) || []).filter(
      (token) => !SELECTOR_PSEUDO_KEYWORDS.has(token)
    )
  )
);

function htmlNeedsScrollAnimations(html) {
  const source = String(html || '');
  if (!source) return false;

  if (typeof DOMParser !== 'undefined') {
    try {
      const doc = new DOMParser().parseFromString(`<div>${source}</div>`, 'text/html');
      for (const selector of RUNTIME_MARKER_SELECTORS) {
        try {
          if (doc.querySelector(selector)) return true;
        } catch {
          // Selector unsupported by this parser — sibling markers cover it
          // (.landscape_layer stands in for the :has() host selector).
        }
      }
      return false;
    } catch {
      // Parse failed — fall through to the token check below.
    }
  }

  // Substring fallback for DOMParser-less contexts. Deliberately fails OPEN,
  // same doctrine as motion-static-css.js htmlCarriesStamp(): a false positive
  // ships a small runtime that no-ops without markers, while a false negative
  // leaves a below-fold reveal element invisible once M6's gated hiding CSS
  // exists. Browser surfaces always take the DOMParser branch above.
  return RUNTIME_MARKER_TOKENS.some((token) => source.includes(token));
}

function collectStateSections(state) {
  const sections = [];
  if (state?.sharedSections?.navigation) sections.push(state.sharedSections.navigation);
  if (Array.isArray(state?.pages)) {
    for (const page of state.pages) {
      if (Array.isArray(page?.sections)) sections.push(...page.sections);
    }
  }
  if (state?.sharedSections?.footer) sections.push(state.sharedSections.footer);
  return sections;
}

export function generateScrollAnimationShimJS() {
  return getScrollAnimationRuntimeSource();
}

export function sectionNeedsScrollAnimations(section) {
  return htmlNeedsScrollAnimations(section?.html);
}

export function needsScrollAnimationShim(input) {
  if (!input) return false;
  if (typeof input === 'string') return htmlNeedsScrollAnimations(input);
  if (Array.isArray(input)) return input.some(sectionNeedsScrollAnimations);
  if (typeof input.html === 'string') return sectionNeedsScrollAnimations(input);
  // The STATE branch must also ask about builder entrances (EA-2). Marker
  // detection cannot see them: sections hold UNSTAMPED html at this point —
  // stamping happens at emission — so a project whose only motion is a builder
  // preset would ship stamped markup with no runtime to reveal it.
  if (stateEmitsEntrance(input)) return true;
  return collectStateSections(input).some(sectionNeedsScrollAnimations);
}

// ── template reveal-convention detection (EA-1) ──────────────────────────────
//
// Which of the TEMPLATE's own scroll-reveal conventions a project actually
// contains, so the drawer can name them and the stamping engine can refuse to
// put a builder entrance on a section that already animates itself (D8).
//
// Reveal-bearing NON-builder patterns only. `mos` has no reveal row — it is
// runtime transform normalization, not a reveal convention — so it is excluded
// here for the same reason it is excluded from the copy the user reads.

const EMPTY_PATTERNS = Object.freeze([]);

const TEMPLATE_REVEAL_PATTERNS = MOTION_PATTERNS.filter(
  (pattern) => pattern.owner !== 'builder' && (pattern.reveal || []).length > 0
).map((pattern) => ({
  id: pattern.id,
  selectors: (pattern.reveal || []).map((group) => group.selector)
}));

/**
 * HTML-aware detection cache (D10).
 *
 * Live section objects are MUTATED IN PLACE — only history patches are cloned
 * (`core/state.js`) — so a WeakMap keyed on identity alone goes stale the moment
 * an edit rewrites `section.html` while the object stays the same. The cached
 * entry therefore carries the html it was computed from and is reused only when
 * that string still matches. It is never invalidated on `state-changed`: that
 * event fires on every `update()`, including inside a transaction, so a cache
 * keyed off it would never survive a scrub (the 16C2 rule).
 */
const templateRevealCache = typeof WeakMap === 'function' ? new WeakMap() : null;

let templateRevealScans = 0;

/** Test hook: how many sections have actually been parsed. */
export function templateRevealScanCount() {
  return templateRevealScans;
}

function scanSectionForTemplateReveals(html) {
  const source = String(html || '');
  const hits = [];
  if (!source) return hits;

  if (typeof DOMParser !== 'undefined') {
    let doc = null;
    try {
      doc = new DOMParser().parseFromString(`<div>${source}</div>`, 'text/html');
    } catch {
      // Fall through to the closed DOMParser-less path below.
    }
    if (doc) {
      for (const pattern of TEMPLATE_REVEAL_PATTERNS) {
        for (const selector of pattern.selectors) {
          let matched = false;
          try {
            matched = !!doc.querySelector(selector);
          } catch {
            // Selector unsupported by this parser — a sibling group covers it.
          }
          if (matched) {
            hits.push(pattern.id);
            break;
          }
        }
      }
      return hits;
    }
  }

  // DOMParser-less fallback. Unlike the shim's emission check this one fails
  // CLOSED: a false positive here would silently refuse to stamp a section the
  // user asked to animate, which is worse than missing a double-animation that
  // only the browser surfaces can actually produce.
  return hits;
}

function templateRevealsForSection(section) {
  const html = typeof section?.html === 'string' ? section.html : '';
  if (!templateRevealCache) return scanSectionForTemplateReveals(html);
  const entry = templateRevealCache.get(section);
  if (entry && entry.html === html) return entry.result;
  templateRevealScans += 1;
  const result = Object.freeze(scanSectionForTemplateReveals(html));
  templateRevealCache.set(section, { html, result });
  return result;
}

/**
 * The template reveal conventions ONE section carries, as a frozen ordered
 * array. Used at stamping time for the one-engine-per-section rule (D8).
 */
export function sectionTemplateRevealPatterns(section) {
  if (!section || typeof section !== 'object') return EMPTY_PATTERNS;
  return templateRevealsForSection(section);
}

/**
 * @param {object} state
 * @returns {{patternIds: ReadonlyArray<string>, sectionCount: number}}
 *   `patternIds` is a frozen ordered array — never a shared mutable Set — in
 *   registry order, and `sectionCount` counts PAGE sections carrying at least
 *   one convention. Shared nav/footer are excluded: v1 never offers a builder
 *   entrance there, so naming their conventions in the drawer would describe
 *   something the toggle beside it cannot change.
 */
export function detectTemplateRevealConventions(state) {
  const found = new Set();
  let sectionCount = 0;
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      const hits = sectionTemplateRevealPatterns(section);
      if (hits.length === 0) continue;
      sectionCount += 1;
      for (const id of hits) found.add(id);
    }
  }
  const patternIds = TEMPLATE_REVEAL_PATTERNS.filter((pattern) => found.has(pattern.id)).map(
    (pattern) => pattern.id
  );
  return { patternIds: Object.freeze(patternIds), sectionCount };
}

/**
 * ONE ENGINE PER SECTION (D8) — the single decision every emission surface asks.
 *
 * If the template's own scroll reveal is still enabled AND this section's markup
 * carries a registered template reveal marker, the builder does not stamp it.
 * Layering two reveal engines on one section is not a smaller version of the
 * feature, it is a section that fades in twice; the product answer is Swap,
 * which turns the template convention off in the same transaction that sets the
 * preset, at which point this function starts returning the preset.
 *
 * Lives here rather than in `shared/entrance-presets.js` on purpose: the
 * detection half needs the motion registry and a DOM parser, and pulling that
 * into the pure module would drag the whole runtime source into every Worker
 * that only wanted to normalize a state field.
 *
 * EA-6 widens the return: a section with ANY valid element entrance is in
 * ELEMENT MODE — `{preset: null, stagger, elements}` — and the section/site
 * preset is superseded (stamping both would double-animate nested pairs;
 * contracts §3). D8 applies to BOTH modes: the template's own convention still
 * wins while it is enabled.
 *
 * @returns {{preset: string, stagger: boolean}
 *          |{preset: null, stagger: boolean, elements: object}
 *          |null} null ⇒ do not stamp
 */
export function resolveSectionEntrance(state, section) {
  const templateWins =
    state?.entrance?.template !== false && sectionTemplateRevealPatterns(section).length > 0;
  const elements = sectionElementEntranceMap(section);
  if (elements) {
    if (templateWins) return null;
    return { preset: null, stagger: false, elements };
  }
  const entrance = effectiveEntranceFor(state, section);
  if (!entrance) return null;
  if (templateWins) return null;
  return entrance;
}

/**
 * True when this state would ship the entrance runtime + stylesheet, accounting
 * for the one-engine rule — a project whose only preset lands on sections that
 * all carry template markers stamps nothing and must ship nothing.
 */
export function stateEmitsEntrance(state) {
  if (!stateUsesEntrance(state)) return false;
  for (const page of state?.pages || []) {
    for (const section of page?.sections || []) {
      if (resolveSectionEntrance(state, section)) return true;
    }
  }
  return false;
}
